/** @format */

import { ActivatedRouteSnapshot, Params, RouterStateSnapshot } from '@angular/router';
import {
	CriteoService,
	EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE
} from '@shared/data-access/product-recommendations';
import {
	Injectable,
	PLATFORM_ID,
	REQUEST_CONTEXT,
	StateKey,
	TransferState,
	computed,
	inject,
	makeStateKey
} from '@angular/core';
import { KillswitchService, Logger } from '@shared/data-access/services';
import { Observable, of } from 'rxjs';
import {
	ProductListApiService,
	ProductListCompareService,
	ProductListMlService,
	ProductListUIService
} from '@plp/data-access/services';
import { ProductListSEO, ProductTypes } from '@shared/data-access/interfaces';
import { catchError, filter, mergeMap, take, tap } from 'rxjs/operators';
import { familyPageDropdownValues, plpDefaultSortMap } from '@plp/data-access/constants';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
	productListResolverFeatureFlagsSet,
	selectFeatureFlags,
	setAllFilters,
	setCriteoProducts,
	setDisplayableProductListData,
	setInitialPageNumber,
	setInitialPageSize,
	setIsProductListPageLoading,
	setIsProductListPageValid,
	setPinnedContent,
	setProductList,
	setProductListSEO,
	setSelectedSort
} from '@plp/data-access/state';

import { ProductList } from '@plp/data-access/interfaces';
import { SeoService } from '@seo/data-access';
import { Store } from '@ngrx/store';
import { setCommonPageType } from '@state/data-access';

@Injectable({
	providedIn: 'root'
})
export class ProductListPageResolveGuard {
	private readonly criteoService = inject(CriteoService);
	private readonly killSwitchService = inject(KillswitchService);
	private readonly log = inject(Logger);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly plpCompareService = inject(ProductListCompareService);
	private readonly plpMlService = inject(ProductListMlService);
	private readonly productListApiService = inject(ProductListApiService);
	private readonly productListUIService = inject(ProductListUIService);
	private readonly propagateToServer = inject<{
		PROPAGATE_ERROR_TO_SERVER: (propagatedError: {
			errorType: string;
			redirectUrl?: string;
		}) => { errorType: string; redirectUrl?: string };
	}>(REQUEST_CONTEXT, { optional: true })?.PROPAGATE_ERROR_TO_SERVER;
	private readonly seoService = inject(SeoService);
	private readonly state = inject(TransferState);
	private readonly store = inject(Store);
	private readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

	private readonly isUsingCriteoSPAAdServer = computed(
		() => this.featureFlags().isUsingCriteoSPAAdServer
	);

	showPinnedContent = true;
	showPinnedProducts = true;
	fulfillmentFacetsEnabled = false;

	constructor() {
		this.store.dispatch(setCommonPageType({ pageType: 'plp' }));
		this.killSwitchService
			.getAllKillSwitchesForCurrentChain$()
			.pipe(take(1))
			.subscribe((data) => {
				this.store.dispatch(productListResolverFeatureFlagsSet({ featureFlags: data }));
				this.showPinnedContent = data.showPinnedContent;
				this.showPinnedProducts = data.showPinnedProducts;
				this.fulfillmentFacetsEnabled = data.fulfillmentFacetsEnabled;
			});
	}

	resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
		return this.seoService.getSEOData$(route.params['seoToken'], 1, true).pipe(
			mergeMap((productListSEO: { categories: ProductListSEO[] }) => {
				// keep the loading spinner off on PLP, just reserved for SRLP.
				this.store.dispatch(
					setIsProductListPageLoading({ isProductListPageLoading: false })
				);

				if (typeof productListSEO !== 'undefined' && Object.keys(productListSEO).length) {
					if (productListSEO?.categories?.length) {
						this.handlePageRedirect(productListSEO);

						this.store.dispatch(
							setProductListSEO({ productListSEO: productListSEO.categories[0] })
						);

						this.getDisplayableProductListDataFromState();

						// Only execute in the Server to avoid duplicated tags
						if (isPlatformServer(this.platformId)) {
							this.seoService.initializePageSEO(
								productListSEO,
								state.url.split('?')[0],
								'family'
							);
						}

						// MAKE CALL FOR PRODUCT LIST DATA
						return this.makeCallForProductListData(
							productListSEO.categories[0].catgroupId,
							route.params['seoToken'],
							route.queryParams,
							productListSEO.categories[0]
						);
					} else {
						this.store.dispatch(
							setIsProductListPageValid({ isProductListPageValid: false })
						);
					}
				}

				return of(null);
			}),
			catchError((error) => {
				if (isPlatformServer(this.platformId) && error?.errorType === 'redirect') {
					throw error;
				}

				this.store.dispatch(
					setIsProductListPageLoading({ isProductListPageLoading: false })
				);
				this.store.dispatch(setIsProductListPageValid({ isProductListPageValid: false }));

				this.log.error(
					`APPLICATION_ERROR:HTTP_ERROR: Error getting page by SEO TOKEN: ${route.params['seoToken']}. ${this.getErrorToPrint(error)}`
				);

				return of(null);
			})
		);
	}

	/**
	 * Handles the redirection of the page based on the product list SEO information.
	 * If the page type is 'c' (category) or 's' (static) or 'f' (family), it redirects to the corresponding SEO URL.
	 * Otherwise, it dispatches an action to set the product list page as valid.
	 *
	 * @param productListSEO - The SEO information of the product list, which includes categories with page type and SEO URL.
	 */
	handlePageRedirect(productListSEO: { categories: Partial<ProductListSEO>[] }): void {
		if (
			isPlatformBrowser(this.platformId) ||
			!productListSEO.categories[0].pageType ||
			!productListSEO.categories[0].seoUrl
		) {
			return;
		}

		const pageType = productListSEO.categories[0].pageType;
		const seoUrl = productListSEO.categories[0].seoUrl;
		const isRedirect = productListSEO.categories[0].redirect;
		if (seoUrl && ((pageType === 'f' && isRedirect) || pageType === 'c' || pageType === 's')) {
			this.propagateToServer({
				errorType: 'redirect',
				redirectUrl: `/${pageType}/${seoUrl}`
			});
			throw { errorType: 'redirect', redirectUrl: `/${pageType}/${seoUrl}` };
		}
		this.store.dispatch(setIsProductListPageValid({ isProductListPageValid: true }));
	}

	/**
	 * Retrieves the displayable product list data from the state and dispatches it to the store.
	 * If the data is found in the state, it dispatches an action to set the displayable product list data
	 * and then removes the data from the state.
	 *
	 * @returns {void}
	 */
	private getDisplayableProductListDataFromState(): void {
		const DISPLAYABLE_PRODUCT_LIST_DATA_KEY: StateKey<any> = makeStateKey(
			'DISPLAYABLE_PRODUCT_LIST_DATA'
		);
		const dpld = this.state.get(DISPLAYABLE_PRODUCT_LIST_DATA_KEY, null);
		if (dpld) {
			this.store.dispatch(
				setDisplayableProductListData({ displayableProductListData: dpld })
			);
			this.state.remove(DISPLAYABLE_PRODUCT_LIST_DATA_KEY);
		}
	}

	/**
	 * @description
	 * Accepts an error and stringifies the important parts
	 */
	private getErrorToPrint(error: any): string {
		try {
			return JSON.stringify(error, ['status', 'message', 'arguments', 'type', 'name']);
		} catch {
			return '';
		}
	}

	/**
	 * @description
	 * Make call for Product List for the PLP product Grid once the seo data is set
	 */
	private makeCallForProductListData(
		catgroupId: number,
		seoUrlToken: string,
		urlQueryParams: Params,
		categories: ProductListSEO
	): Observable<ProductList | null> {
		const pageNumber = this.productListUIService.getPageNumber(urlQueryParams?.pageNumber);
		this.store.dispatch(setInitialPageNumber({ pageNumber }));

		const pageSize = this.productListUIService.getPageSize(urlQueryParams?.pageSize);
		this.store.dispatch(setInitialPageSize({ pageSize }));

		const selectedFilters =
			this.productListApiService.formatFiltersFromURL(urlQueryParams.filterFacets) ?? {};
		this.store.dispatch(setAllFilters({ selectedFilters }));

		this.getAndSetSelectedSort(categories, urlQueryParams?.selectedSort);

		const queryParams = this.productListApiService.setProductListParamDataForApi(
			catgroupId,
			null,
			pageNumber,
			selectedFilters,
			pageSize,
			this.fulfillmentFacetsEnabled
		);
		return this.productListApiService
			.getProductListDataFromApi$(queryParams, 1, this.showPinnedProducts)
			.pipe(
				take(1),
				filter(
					(productList: ProductList | null) =>
						typeof productList !== 'undefined' && productList !== null
				),
				tap((productList: ProductList | null) => {
					this.plpMlService.generateNewMlSearchEventParentUUID();

					this.store.dispatch(setProductList({ productList }));
					if (!this.plpCompareService.hasTheComparePaintedDoorTestBeenOpened()) {
						this.plpCompareService.getCompareListFromSessionStorage();
					}
					if (this.isUsingCriteoSPAAdServer() && categories?.attr?.BREADCRUMB?.value) {
						this.getAndSetCriteoProducts(
							categories,
							pageNumber,
							pageSize,
							selectedFilters
						);
					} else {
						this.store.dispatch(
							setCriteoProducts({
								criteoProducts: EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE
							})
						);
					}
					this.getAndSetPinnedContent(productList, seoUrlToken);
				}),
				catchError((error) => {
					this.log.error(
						`Search API error for category ID ${catgroupId} with the following error: ${this.getErrorToPrint(error)}`
					);

					const pl = this.getProductListFromState();
					if (pl) {
						this.store.dispatch(setProductList({ productList: pl }));
					}

					return of(null);
				})
			);
	}

	/**
	 * Retrieves the product list data from the application state.
	 *
	 * This method searches through the state object for a specific key that contains
	 * the 'searchVO' string within its 'u' property. Once found, it extracts and returns
	 * the associated 'b' property value as the product list data.
	 */
	private getProductListFromState(): ProductList | null {
		const state: { u: string; b: string }[] = this.state['store'];
		let productListData = null;

		for (const val of Object.values(state)) {
			if (val.u?.includes('searchVO')) {
				productListData = val.b;
				break;
			}
		}

		return productListData;
	}

	private getAndSetCriteoProducts(
		categories: ProductListSEO,
		pageNumber: number,
		listSize: number,
		filters = null
	): void {
		if (!isPlatformBrowser(this.platformId)) {
			this.store.dispatch(
				setCriteoProducts({ criteoProducts: EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE })
			);
			return;
		}
		const formattedBreadcrumb = this.criteoService.transformToCriteoBreadcrumbFormat(
			categories.attr.BREADCRUMB.value
		);
		const criteoFilters =
			filters && Object.keys(filters).length > 0
				? this.criteoService.parseFilters(filters)
				: null;
		this.criteoService
			.viewProductsPage({
				productType: ProductTypes.VIEW_CATEGORY,
				category: formattedBreadcrumb,
				listSize,
				pageNumber,
				filters: criteoFilters
			})
			.subscribe({
				next: (res) => {
					this.criteoService.fireBeacon(res?.placements[0]?.carousel[0]?.OnLoadBeacon);
					this.store.dispatch(setCriteoProducts({ criteoProducts: res }));
				},
				error: (err) => {
					console.error('Error fetching Criteo data:', err);
					this.store.dispatch(
						setCriteoProducts({ criteoProducts: EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE })
					);
				}
			});
	}

	private getAndSetPinnedContent(productListData: ProductList, seoUrlToken: string): void {
		if (this.showPinnedContent) {
			this.productListUIService.setPinnedContentHomrDataToState(productListData, seoUrlToken);
			return;
		}

		this.store.dispatch(setPinnedContent({ pinnedContent: {} }));
	}

	private getAndSetSelectedSort(
		productListSEO: ProductListSEO,
		qpSelectedSort: string | undefined
	): void {
		const defaultSortMap = plpDefaultSortMap;

		const sortDropdownValues = familyPageDropdownValues;

		const queryParamSortToNumber: number = qpSelectedSort ? parseInt(qpSelectedSort, 10) : 0;
		const sortBasedOnQueryParam = {
			label: sortDropdownValues[queryParamSortToNumber]?.label,
			value: queryParamSortToNumber
		};

		const defaultSortFromSEO: number = defaultSortMap[productListSEO.longDescription];
		const sortBasedOnSEOData = {
			label: sortDropdownValues[defaultSortFromSEO]?.label,
			value: defaultSortFromSEO
		};

		const selectedSort: { label: string; value: number } = qpSelectedSort
			? sortBasedOnQueryParam
			: sortBasedOnSEOData;

		this.store.dispatch(setSelectedSort({ selectedSort }));
	}
}
